import * as React from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Drawer from '@material-ui/core/Drawer';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { DesktopAppBar } from './DesktopAppBar';
import { DrawerLinksContainer } from '../../containers/Nav/DrawerLinks';
import { Location } from 'history';
import { DeepReadonly } from 'utility-types';
import { IUserOrganization } from '@pec/aion-ui-core/interfaces/userOrganization';
import { IOrganization } from '@pec/aion-ui-core/interfaces/organization';

const expandedWidth = 290;
const collapsedWidth = 64;

// Comes from theme.mixins.toolbar['@media (min-width:600px)'].minHeight
const toolbarHeight = 64;

export const styles = (theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      zIndex: 1,
      position: 'relative',
      display: 'flex'
    },
    drawerPaper: {
      display: 'block',
      overflowX: 'hidden',
      paddingTop: toolbarHeight,
      width: collapsedWidth,
      transition: theme.transitions.create('width', {
        duration: theme.transitions.duration.enteringScreen
      })
    },
    expanded: {
      width: expandedWidth
    },
    icon: {
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.text.primary
      },
      cursor: 'pointer',
      position: 'fixed',
      top: '50%',
      transition: theme.transitions.create('left', {
        duration: theme.transitions.duration.enteringScreen
      })
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      marginLeft: expandedWidth,
      padding: theme.spacing(1),
      paddingTop: toolbarHeight,
      transition: theme.transitions.create('margin', {
        duration: theme.transitions.duration.enteringScreen
      }),
      width: 0
    },
    contentShift: {
      marginLeft: collapsedWidth
    }
  });

export type OwnProps = {
  sideNav?: JSX.Element;
  handleLogOut: () => void;
  userName: string | null;
  userId?: string;
  userEmail: string | null;
  includeCompanySearch: boolean;
  organizationId?: string;
  handleExpand: () => void;
  expanded: boolean;
  location: Location;
  userOrganizations: DeepReadonly<IUserOrganization[]> | null;
  organization: DeepReadonly<IOrganization> | null;
  hasFetchedUserOrganizations: boolean;
};

export type Props = WithStyles<typeof styles> & OwnProps;

const DesktopNav: React.FC<Props> = ({
  classes,
  includeCompanySearch,
  handleLogOut,
  userId,
  children,
  sideNav,
  organizationId,
  handleExpand,
  expanded,
  location,
  userOrganizations,
  organization,
  hasFetchedUserOrganizations,
  userEmail
}) => {
  const { t } = useTranslation();
  return (
    <div className={classes.root}>
      <DesktopAppBar
        includeCompanySearch={includeCompanySearch}
        handleLogOut={handleLogOut}
        userEmail={userEmail}
        userId={userId}
        organizationId={organizationId}
        userOrganizations={userOrganizations}
        organization={organization}
        hasFetchedUserOrganizations={hasFetchedUserOrganizations}
      />
      <div
        style={{
          left: (expanded ? expandedWidth : collapsedWidth) - 4
        }}
        className={classes.icon}
        aria-label={
          expanded ? t('components.desktopNav.collapse', 'Collapse') : t('components.desktopNav.expand', 'Expand')
        }
        onClick={handleExpand}
      >
        {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </div>
      <Drawer
        variant="permanent"
        classes={{
          paper: `${classes.drawerPaper} ${expanded ? classes.expanded : ''}`
        }}
      >
        <DrawerLinksContainer
          navExpanded={expanded}
          organizationId={organizationId}
          sideNav={sideNav}
          location={location}
        />
      </Drawer>
      <div className={`${classes.content} ${expanded ? '' : classes.contentShift}`}>{children}</div>
    </div>
  );
};

export const DesktopNavComponent = withStyles(styles)(DesktopNav);
